import type { AssetId } from '@shapeshiftoss/caip'
import { adapters, fromAssetId, gnosisChainId } from '@shapeshiftoss/caip'

import { createBanxaUrl, getSupportedBanxaFiatCurrencies } from './fiatRampProviders/banxa'
import {
  createCoinbaseUrl,
  getCoinbaseSupportedAssets,
  getSupportedCoinbaseFiatCurrencies,
} from './fiatRampProviders/coinbase'
import {
  createMtPelerinUrl,
  getMtPelerinAssets,
  getMtPelerinFiatCurrencies,
  getMtPelerinQuote,
} from './fiatRampProviders/mtpelerin'
import { createOnRamperUrl, getOnRamperAssets } from './fiatRampProviders/onramper'
import { SUPPORTED_ONRAMPER_FIAT_CURRENCIES } from './fiatRampProviders/onramper/constants'
import type { CreateUrlProps } from './types'

import banxaLogo from '@/assets/banxa.png'
import CoinbaseLogo from '@/assets/coinbase-logo.svg'
import MtPelerinLogo from '@/assets/mtpelerin.png'
import OnRamperLogo from '@/assets/onramper-logo.svg'
import { getBanxaQuote } from '@/components/Modals/FiatRamps/fiatRampProviders/banxa/utils'
import { getOnramperQuote } from '@/components/Modals/FiatRamps/fiatRampProviders/onramper/utils'
import type { CommonFiatCurrencies, FiatCurrencyItem } from '@/lib/fiatCurrencies/fiatCurrencies'
import type { FeatureFlags } from '@/state/slices/preferencesSlice/preferencesSlice'

export const usdcAssetId: AssetId = 'eip155:1/erc20:0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48'

export type RampQuote = {
  id: string
  provider: FiatRamp
  providerLogo?: string
  fiatFee?: string
  networkFee?: string
  rate: string
  amount: string
  isBestRate?: boolean
  isCreditCard?: boolean
  isBankTransfer?: boolean
  isApplePay?: boolean
  isGooglePay?: boolean
  isSepa?: boolean
}

export type GetQuotesArgs = {
  fiatCurrency: FiatCurrencyItem
  crypto: string
  amount: string
  direction: 'buy' | 'sell'
}

export interface SupportedFiatRampConfig {
  id: FiatRamp
  // key of translation jsons, will be used to show the provider name in the list
  label: string
  // key of translation jsons, will be used to show the provider info in the list
  info?: string
  // array of keys of translation jsons, will be used to show the tags in the list
  tags?: string[]
  logo: string
  // manul sort of the ramps
  order: number
  isActive: (featureFlags: FeatureFlags) => boolean
  getBuyAndSellList: () => Promise<[AssetId[], AssetId[]]>
  getSupportedFiatList: () => CommonFiatCurrencies[]
  getQuotes?: (args: GetQuotesArgs) => Promise<RampQuote | null>
  onSubmit: (args: CreateUrlProps) => Promise<string | undefined>
  minimumSellThreshold?: number
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fiatRamps = ['Banxa', 'MtPelerin', 'OnRamper', 'Coinbase'] as const
export type FiatRamp = (typeof fiatRamps)[number]
export type SupportedFiatRamp = Record<FiatRamp, SupportedFiatRampConfig>

export const supportedFiatRamps: SupportedFiatRamp = {
  Coinbase: {
    id: 'Coinbase',
    label: 'fiatRamps.coinbase',
    logo: CoinbaseLogo,
    order: 3,
    getBuyAndSellList: () => {
      const buyList = getCoinbaseSupportedAssets().buy
      const sellList = getCoinbaseSupportedAssets().sell
      return Promise.resolve([buyList, sellList])
    },
    getSupportedFiatList: () => getSupportedCoinbaseFiatCurrencies(),
    onSubmit: props => {
      return Promise.resolve(createCoinbaseUrl(props))
    },
    isActive: () => true,
    minimumSellThreshold: 0,
  },
  OnRamper: {
    id: 'OnRamper',
    label: 'fiatRamps.onRamper',
    tags: ['Aggregator'],
    logo: OnRamperLogo,
    isActive: () => true,
    minimumSellThreshold: 0,
    order: 3,
    getBuyAndSellList: async () => {
      const buyAndSellAssetIds = await getOnRamperAssets()
      // Gnosis network is listed in supported assets, but is not currently working (pending support ticket response)
      const filteredBuyAndSellAssetIds = buyAndSellAssetIds.filter(
        assetId => fromAssetId(assetId).chainId !== gnosisChainId,
      )
      return [filteredBuyAndSellAssetIds, filteredBuyAndSellAssetIds]
    },
    getSupportedFiatList: () => SUPPORTED_ONRAMPER_FIAT_CURRENCIES,
    getQuotes: getOnramperQuote,
    onSubmit: async props => {
      try {
        const onRamperCheckoutUrl = await createOnRamperUrl(props)
        return onRamperCheckoutUrl
      } catch (err) {
        console.error(err)
        return Promise.resolve(undefined)
      }
    },
  },
  Banxa: {
    id: 'Banxa',
    label: 'fiatRamps.banxa',
    logo: banxaLogo,
    isActive: () => true,
    minimumSellThreshold: 50,
    order: 2,
    getBuyAndSellList: () => {
      const buyAssetIds = adapters.getSupportedBanxaAssets().map(({ assetId }) => assetId)
      const sellAssetIds = adapters.getSupportedBanxaSellAssets()
      return Promise.resolve([buyAssetIds, sellAssetIds])
    },
    getSupportedFiatList: () => getSupportedBanxaFiatCurrencies(),
    getQuotes: getBanxaQuote,
    onSubmit: props => {
      try {
        const banxaCheckoutUrl = createBanxaUrl(props)
        return Promise.resolve(banxaCheckoutUrl)
      } catch (err) {
        console.error(err)
        return Promise.resolve(undefined)
      }
    },
  },
  MtPelerin: {
    id: 'MtPelerin',
    label: 'fiatRamps.mtPelerin',
    tags: ['fiatRamps.noKYC', 'fiatRamps.nonUS'],
    logo: MtPelerinLogo,
    order: 5,
    isActive: () => true,
    // https://developers.mtpelerin.com/service-information/pricing-and-limits#limits-2
    // 50 CHF is currently equivalent to 51.72 USD
    // note that Mt Pelerin has a minimum of 50 CHF, and our fiat balance is denoted in USD
    minimumSellThreshold: 55,
    getBuyAndSellList: async () => {
      const buyAndSellAssetIds = await getMtPelerinAssets()
      return [buyAndSellAssetIds, buyAndSellAssetIds]
    },
    getSupportedFiatList: () => getMtPelerinFiatCurrencies(),
    getQuotes: getMtPelerinQuote,
    onSubmit: props => {
      try {
        const mtPelerinCheckoutUrl = createMtPelerinUrl(props)
        return Promise.resolve(mtPelerinCheckoutUrl)
      } catch (err) {
        console.error(err)
        return Promise.resolve(undefined)
      }
    },
  },
}
